import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  List,
  Avatar,
  Tag,
  Typography,
  Spin,
  Empty,
  Tooltip,
  message,
  Button,
  Space,
  Popconfirm,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  GiftOutlined,
  TeamOutlined,
  CopyOutlined,
  QrcodeOutlined,
  CloseCircleOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { appointmentAPI, doctorAPI } from "../../services/api";
import { PageHero } from "../../components/Illustration";

const { Text } = Typography;

const DoctorHome = () => {
  const { doctorId } = useParams();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doctorProfile, setDoctorProfile] = useState(null);

  useEffect(() => {
    if (!doctorId) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [apptRes, profileRes] = await Promise.allSettled([
          appointmentAPI.getByDoctor(doctorId),
          doctorAPI.getByUserId(doctorId),
        ]);
        if (apptRes.status === "fulfilled") {
          const data =
            apptRes.value?.data?.data?.appointments ||
            apptRes.value?.data?.appointments ||
            (Array.isArray(apptRes.value?.data) ? apptRes.value.data : []);
          setAppointments(data);
        }
        // console.log(profileRes, "profileRes.value?.data?.data");
        if (profileRes.status === "fulfilled") {
          setDoctorProfile(profileRes.value?.data || null);
        }
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [doctorId]);

  const today = new Date().toDateString();

  const todayAppointments = appointments.filter(
    (a) => new Date(a.appointmentDate).toDateString() === today
  );

  const stats = [
    {
      title: "Today's Appointments",
      value: todayAppointments.length,
      icon: <CalendarOutlined style={{ fontSize: 24, color: "#0A6EBD" }} />,
      color: "#F0F9FF",
    },
    {
      title: "Completed",
      value: appointments.filter((a) => a.status === "completed").length,
      icon: <CheckCircleOutlined style={{ fontSize: 24, color: "#0A6EBD" }} />,
      color: "#F0F9FF",
    },
    {
      title: "Pending",
      value: appointments.filter((a) => a.status === "pending").length,
      icon: <ClockCircleOutlined style={{ fontSize: 24, color: "#0A6EBD" }} />,
      color: "#F0F9FF",
    },
    {
      title: "Total Patients",
      value: new Set(appointments.map((a) => a.patient?._id).filter(Boolean))
        .size,
      icon: <UserOutlined style={{ fontSize: 24, color: "#0A6EBD" }} />,
      color: "#F0F9FF",
    },
    {
      title: "Referral Points",
      value: doctorProfile?.referralPoints ?? 0,
      icon: <GiftOutlined style={{ fontSize: 24, color: "#0A6EBD" }} />,
      color: "#F0F9FF",
    },
    {
      title: "Total Referrals",
      value: doctorProfile?.totalReferrals ?? 0,
      icon: <TeamOutlined style={{ fontSize: 24, color: "#0A6EBD" }} />,
      color: "#F0F9FF",
    },
  ];

  const handleCancel = async (aptId) => {
    try {
      await appointmentAPI.update(aptId, { status: "cancelled" });
      message.success("Appointment cancelled");
      setAppointments((prev) =>
        prev.map((a) => (a._id === aptId ? { ...a, status: "cancelled" } : a))
      );
    } catch {
      message.error("Failed to cancel appointment");
    }
  };

  const handleComplete = async (aptId) => {
    try {
      await appointmentAPI.update(aptId, { status: "completed" });
      message.success("Appointment marked as completed");
      setAppointments((prev) =>
        prev.map((a) => (a._id === aptId ? { ...a, status: "completed" } : a))
      );
    } catch {
      message.error("Failed to update appointment");
    }
  };

  const statusColor = (status) => {
    const map = {
      pending: "orange",
      confirmed: "blue",
      completed: "green",
      cancelled: "red",
    };
    return map[status?.toLowerCase()] || "default";
  };

  return (
    <div>
      <PageHero title="Doctor Dashboard" subtitle="Today's appointments, patients and referrals at a glance." scene="consult" />

      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={[24, 24]}>
            {stats.map((stat, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card
                  style={{
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Statistic title={stat.title} value={stat.value} />
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 12,
                        background: stat.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {stat.icon}
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Referral Code Card — always shown once profile loads */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card
                style={{
                  borderRadius: 12,
                  background: "#F0F9FF",
                  border: "1px solid #667eea30",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 16,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 16 }}
                  >
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 12,
                        background: "#DBEAFE",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <QrcodeOutlined
                        style={{ fontSize: 28, color: "#0A6EBD" }}
                      />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        Your Referral Code
                      </Text>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          marginTop: 2,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 28,
                            fontWeight: 800,
                            letterSpacing: 4,
                            color: "#0A6EBD",
                          }}
                        >
                          {doctorProfile?.referralCode || "—"}
                        </span>
                        {doctorProfile?.referralCode && (
                          <Tooltip title="Copy code">
                            <CopyOutlined
                              style={{
                                fontSize: 18,
                                color: "#0A6EBD",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                navigator.clipboard?.writeText(
                                  doctorProfile.referralCode
                                );
                                message.success("Referral code copied!");
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {doctorProfile?.referralCode
                          ? "Share this code with patients for lab test bookings"
                          : "Referral code is being generated — refresh in a moment"}
                      </Text>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 24 }}>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: 24,
                          fontWeight: 700,
                          color: "#0A6EBD",
                        }}
                      >
                        {doctorProfile?.referralPoints ?? 0}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Points Earned
                      </Text>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: 24,
                          fontWeight: 700,
                          color: "#0A6EBD",
                        }}
                      >
                        {doctorProfile?.totalReferrals ?? 0}
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Total Referrals
                      </Text>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card
                title={
                  <span>
                    <CalendarOutlined
                      style={{ marginRight: 8, color: "#0A6EBD" }}
                    />
                    Today's Schedule
                    <Tag color="blue" style={{ marginLeft: 10 }}>
                      {todayAppointments.length} appointments
                    </Tag>
                  </span>
                }
                style={{
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                {todayAppointments.length === 0 ? (
                  <Empty
                    description="No appointments scheduled for today"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <List
                    dataSource={todayAppointments.sort((a, b) =>
                      (a.appointmentTime || "").localeCompare(
                        b.appointmentTime || ""
                      )
                    )}
                    renderItem={(apt) => (
                      <List.Item
                        extra={
                          <Space>
                            <Tag color={statusColor(apt.status)}>
                              {apt.status?.toUpperCase()}
                            </Tag>
                            {apt.status !== "cancelled" && apt.status !== "completed" && (
                              <>
                                <Popconfirm
                                  title="Mark this appointment as completed?"
                                  onConfirm={() => handleComplete(apt._id)}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Button size="small" style={{ color: "#0A6EBD", borderColor: "#0A6EBD" }} icon={<CheckOutlined />}>
                                    Complete
                                  </Button>
                                </Popconfirm>
                                <Popconfirm
                                  title="Cancel this appointment?"
                                  onConfirm={() => handleCancel(apt._id)}
                                  okText="Yes"
                                  cancelText="No"
                                >
                                  <Button size="small" danger icon={<CloseCircleOutlined />}>
                                    Cancel
                                  </Button>
                                </Popconfirm>
                              </>
                            )}
                          </Space>
                        }
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              icon={<UserOutlined />}
                              style={{ backgroundColor: "#0A6EBD" }}
                            />
                          }
                          title={
                            <Text strong>{apt.patient?.name || "N/A"}</Text>
                          }
                          description={
                            <span>
                              <ClockCircleOutlined
                                style={{ marginRight: 4, color: "#6B7280" }}
                              />
                              {apt.appointmentTime || "—"}
                              {apt.patient?.phone && (
                                <Text
                                  type="secondary"
                                  style={{ marginLeft: 12, fontSize: 12 }}
                                >
                                  {apt.patient.phone}
                                </Text>
                              )}
                              {apt.transactionId && (
                                <Text
                                  type="secondary"
                                  style={{ marginLeft: 12, fontSize: 11 }}
                                >
                                  TXN: <Text code copyable style={{ fontSize: 11 }}>{apt.transactionId}</Text>
                                </Text>
                              )}
                              {!apt.transactionId && apt.paymentMethod === "cash" && (
                                <Text type="secondary" style={{ marginLeft: 12, fontSize: 11 }}>
                                  Pay at Clinic
                                </Text>
                              )}
                            </span>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default DoctorHome;
